import { IReportIssueProps } from '@common/types/types';
import { Archive } from '@geist-ui/icons';
import { IAdminIssue, fetchIssue, putAdminIssuesArchiveSaga, putIssueStatus, useAppDispatch } from 'src/store';

interface useButtonVisibilityProps {
  status?: string;
  userRole: string;
  item?: IReportIssueProps | IAdminIssue;
  archived?: boolean;
}

interface ButtonsProps {
  label: string;
  onClick: () => void;
  color: 'burgundy' | 'dark-green' | 'gray' | 'gray-light';
  icon?: JSX.Element;
}

const useButtonVisibility = ({
  status = 'opened',
  userRole,
  item,
  archived,
}: useButtonVisibilityProps): ButtonsProps[] => {
  const isOpened = status === 'opened';
  const isReopened = status === 'reopened';
  const isResolved = status === 'resolved';
  const isClosed = status === 'closed';
  const dispatch = useAppDispatch();

  const getWebOwnerButtons = (): ButtonsProps[] => {
    if (isOpened || isReopened) {
      return [
        { label: 'resolveButton', onClick: handleResolve, color: 'burgundy' },
        { label: 'viewDetailsButton', onClick: handleViewDetails, color: 'gray-light' },
      ];
    } else if (isResolved || isClosed) {
      return [{ label: 'viewDetailsButton', onClick: handleViewDetails, color: 'gray-light' }];
    } else {
      return [];
    }
  };

  const getSuperAdminButtons = (): ButtonsProps[] => {
    if (archived) {
      return [{ label: 'unarchiveButton', onClick: handleUnarchive, color: 'gray-light' }];
    } else {
      return [
        { label: 'viewDetailsButton', onClick: handleViewDetails, color: 'gray-light' },
        { label: 'resolveButton', onClick: handleResolve, color: 'burgundy' },
        { label: '', onClick: handleArchive, color: 'gray-light', icon: <Archive size={24} /> },
      ];
    }
  };

  const getUserButtons = (): ButtonsProps[] => {
    if (isOpened || isReopened) {
      return [
        { label: 'closeButton', onClick: handleClose, color: 'dark-green' },
        { label: 'viewDetailsButton', onClick: handleViewDetails, color: 'gray-light' },
      ];
    } else if (isResolved) {
      return [
        { label: 'fixedButton', onClick: handleClose, color: 'burgundy' },
        { label: 'notFixedButton', onClick: handleReopen, color: 'dark-green' },
        { label: 'viewDetailsButton', onClick: handleViewDetails, color: 'gray-light' },
      ];
    } else if (isClosed) {
      return [
        { label: 'reopenButton', onClick: handleReopen, color: 'burgundy' },
        { label: 'viewDetailsButton', onClick: handleViewDetails, color: 'gray-light' },
      ];
    } else {
      return [];
    }
  };

  const getUserViewButtons = (): ButtonsProps[] => {
    if (isOpened || isReopened) {
      return [{ label: 'closeButton', onClick: handleClose, color: 'dark-green' }];
    } else if (isResolved) {
      return [
        { label: 'fixedButton', onClick: handleClose, color: 'burgundy' },
        { label: 'notFixedButton', onClick: handleReopen, color: 'dark-green' },
      ];
    } else if (isClosed) {
      return [{ label: 'reopenButton', onClick: handleReopen, color: 'burgundy' }];
    } else {
      return [];
    }
  };

  const getViewDetailsButtons = (): ButtonsProps[] => {
    if (isOpened || isReopened) {
      return [{ label: 'resolveButton', onClick: handleResolve, color: 'burgundy' }];
    } else {
      return [];
    }
  };

  const handleArchive = (): void => {
    dispatch(putAdminIssuesArchiveSaga({ issueId: [item?.issueId] }));
    dispatch(fetchIssue({ siteId: item?.siteId, issueId: item?.issueId }));
  };

  const handleUnarchive = (): void => {
    //TODO: implement when api is available
    // console.log('unarchive');
  };

  const handleClose = (): void => {
    dispatch(
      putIssueStatus({ siteId: item?.siteId, issueId: item?.issueId, userRole: userRole, data: { status: 'closed' } }),
    );
  };

  const handleViewDetails = (): void => {
    // TODO: implement
  };
  const handleReopen = (): void => {
    dispatch(
      putIssueStatus({
        siteId: item?.siteId,
        issueId: item?.issueId,
        userRole: userRole,
        data: { status: 'reopened' },
      }),
    );
  };

  const handleResolve = async (): Promise<void> => {
    dispatch(
      putIssueStatus({
        siteId: item?.siteId,
        issueId: item?.issueId,
        userRole: userRole,
        data: { status: 'resolved' },
      }),
    );
  };

  if (userRole === 'superadmin') {
    return getSuperAdminButtons();
  } else if (userRole === 'webowner') {
    return getWebOwnerButtons();
  } else if (userRole === 'view') {
    return getViewDetailsButtons();
  } else if (userRole === 'userView') {
    return getUserViewButtons();
  } else {
    return getUserButtons();
  }
};

export default useButtonVisibility;
